<template>
  <div
    class="app-shell"
    :class="{
      'kiosk-mode': shellStore.isKioskSession,
      'app-shell--mobile': shellStore.isMobile,
      'app-shell--desktop': !shellStore.isMobile,
      'app-shell--global-host': shellStore.globalShellHostEnabled,
      'app-shell--chrome-visible': showShellChrome,
    }"
    @click="resetKioskTimeout"
    @keydown="resetKioskTimeout"
  >
    <aside
      v-if="showDesktopSidebar"
      class="app-shell__sidebar"
      :class="{ 'app-shell__sidebar--collapsed': !shellStore.sidebarOpen }"
    >
      <div class="app-shell__sidebar-header">
        <img
          v-if="customLogoUrl"
          :src="customLogoUrl"
          :alt="currentCompanyName"
          class="app-shell__sidebar-logo app-shell__sidebar-logo--custom"
        />
        <img
          v-else
          src="/img/templates/logo-tweem-horiz-blanco.png"
          alt="Tweem"
          class="app-shell__sidebar-logo"
        />
        <button
          type="button"
          class="app-shell__sidebar-toggle"
          @click="shellStore.toggleSidebar()"
        >
          {{ shellStore.sidebarOpen ? "<<" : ">>" }}
        </button>
      </div>

      <div class="app-shell__sidebar-body">
        <button
          type="button"
          class="app-shell__sidebar-profile"
          :class="{
            'app-shell__sidebar-profile--clickable': canSwitchProfiles,
            'app-shell__sidebar-profile--loading': sessionInformationLoading,
          }"
          :disabled="
            sessionInformationLoading || !canSwitchProfiles || switchingProfile
          "
          :aria-label="currentEmployeeName"
          :title="currentEmployeeName"
          :aria-busy="sessionInformationLoading"
          @click="openProfilesDialog"
        >
          <template v-if="sessionInformationLoading">
            <span
              class="app-shell__sidebar-profile-avatar app-shell__skeleton"
              aria-hidden="true"
            />
          </template>
          <img
            v-else
            :src="sidebarAvatarUrl"
            :alt="currentEmployeeName"
            class="app-shell__sidebar-profile-avatar"
            @error="handleSidebarAvatarError"
          />
          <span class="app-shell__sidebar-profile-copy">
            <span
              v-if="sessionInformationLoading"
              class="app-shell__sidebar-profile-name app-shell__skeleton app-shell__skeleton--text app-shell__skeleton--text-medium"
              aria-hidden="true"
            />
            <span v-else class="app-shell__sidebar-profile-name">
              {{ currentEmployeeName }}
            </span>
            <span
              v-if="sessionInformationLoading"
              class="app-shell__sidebar-profile-meta app-shell__skeleton app-shell__skeleton--text app-shell__skeleton--text-short"
              aria-hidden="true"
            />
            <span v-else class="app-shell__sidebar-profile-meta">
              {{ currentCompanyName }}
            </span>
          </span>
          <span
            v-if="!sessionInformationLoading && canSwitchProfiles"
            class="app-shell__sidebar-profile-trailing material-icons"
            aria-hidden="true"
          >
            chevron_right
          </span>
        </button>

        <div class="app-shell__nav-group">
          <button
            v-for="item in primaryNavigationItems"
            :key="item.routeName"
            type="button"
            class="app-shell__sidebar-item"
            :class="{
              'app-shell__sidebar-item--active':
                shellStore.activeRouteName === item.routeName,
            }"
            :aria-label="item.label"
            :title="item.label"
            @click="navigateTo(item.routeName)"
          >
            <span class="app-shell__sidebar-item-leading">
              <span
                class="app-shell__sidebar-icon material-icons"
                aria-hidden="true"
              >
                {{ item.iconName }}
              </span>
              <span class="app-shell__sidebar-item-label">
                {{ item.label }}
              </span>
            </span>
            <span v-if="item.badge > 0" class="app-shell__sidebar-item-badge">
              {{ item.badge }}
            </span>
          </button>
        </div>

        <div class="app-shell__nav-group app-shell__nav-group--secondary">
          <button
            v-for="item in secondaryNavigationItems"
            :key="item.routeName"
            type="button"
            class="app-shell__sidebar-item"
            :class="{
              'app-shell__sidebar-item--active':
                shellStore.activeRouteName === item.routeName,
            }"
            :aria-label="item.label"
            :title="item.label"
            @click="navigateTo(item.routeName)"
          >
            <span class="app-shell__sidebar-item-leading">
              <span
                class="app-shell__sidebar-icon material-icons"
                aria-hidden="true"
              >
                {{ item.iconName }}
              </span>
              <span class="app-shell__sidebar-item-label">
                {{ item.label }}
              </span>
            </span>
            <span v-if="item.badge > 0" class="app-shell__sidebar-item-badge">
              {{ item.badge }}
            </span>
          </button>
        </div>
      </div>

      <div class="app-shell__sidebar-footer">
        <button
          v-if="shellStore.isKioskSession"
          type="button"
          class="app-shell__sidebar-action app-shell__sidebar-action--danger"
          :aria-label="kioskExitLabel"
          :title="kioskExitLabel"
          @click="exitKiosk()"
        >
          <span class="app-shell__sidebar-action-leading">
            <span
              class="app-shell__sidebar-icon material-icons"
              aria-hidden="true"
            >
              exit_to_app
            </span>
            <span class="app-shell__sidebar-action-label">
              {{ kioskExitLabel }}
            </span>
          </span>
        </button>
        <button
          v-if="sessionCatalogStore.isAdmin && !shellStore.isKioskSession"
          type="button"
          class="app-shell__sidebar-action"
          :aria-label="t('wkt.client.main.lateral-menu.admin')"
          :title="t('wkt.client.main.lateral-menu.admin')"
          @click="openAdmin()"
        >
          <span class="app-shell__sidebar-action-leading">
            <span
              class="app-shell__sidebar-icon material-icons"
              aria-hidden="true"
            >
              open_in_new
            </span>
            <span class="app-shell__sidebar-action-label">
              {{ t("wkt.client.main.lateral-menu.admin") }}
            </span>
          </span>
        </button>
        <button
          v-for="item in shellActionItems"
          :key="item.id"
          type="button"
          class="app-shell__sidebar-action"
          :aria-label="item.label"
          :title="item.label"
          @click="item.run()"
        >
          <span class="app-shell__sidebar-action-leading">
            <span
              class="app-shell__sidebar-icon material-icons"
              aria-hidden="true"
            >
              {{ item.iconName }}
            </span>
            <span class="app-shell__sidebar-action-label">
              {{ item.label }}
            </span>
          </span>
        </button>
      </div>
    </aside>

    <header
      v-if="showShellTopbar"
      class="app-shell__topbar"
      :class="{
        'app-shell__topbar--with-sidebar': showDesktopSidebar,
        'app-shell__topbar--contextual': hasContextualBack,
      }"
    >
      <template v-if="shellStore.isMobile">
        <div class="app-shell__mobile-topbar-main">
          <div class="app-shell__mobile-topbar-leading">
            <button
              v-if="hasContextualBack"
              type="button"
              class="app-shell__back-button"
              :aria-label="t('wkt.client.main.back')"
              @click="handleBackNavigation"
            >
              <span class="material-icons" aria-hidden="true">arrow_back</span>
            </button>
            <template v-if="shellStore.activeRouteName === 'home'">
              <img
                v-if="customLogoUrl"
                :src="customLogoUrl"
                :alt="currentCompanyName"
                class="app-shell__header-logo app-shell__header-logo--custom"
              />
              <img
                v-else
                src="/img/templates/logo-tweem-horiz-blanco.png"
                alt="Tweem"
                class="hidden-lg app-shell__header-logo"
                width="120"
              />
            </template>
            <template v-else>
              <h1 class="app-shell__title app-shell__title--mobile">
                {{ currentRouteLabel }}
              </h1>
            </template>
          </div>

          <button
            v-if="showMobileKioskExit"
            type="button"
            class="app-shell__kiosk-exit"
            @click="exitKiosk()"
          >
            {{ kioskExitLabel }}
          </button>

          <button
            v-else-if="showTopbarProfileSwitcher && (sessionInformationLoading || canSwitchProfiles)"
            type="button"
            class="app-shell__profile-switcher"
            :class="{
              'app-shell__profile-switcher--clickable': canSwitchProfiles,
              'app-shell__profile-switcher--loading': sessionInformationLoading,
            }"
            :disabled="
              sessionInformationLoading ||
              !canSwitchProfiles ||
              switchingProfile
            "
            :aria-label="currentCompanyName"
            :title="currentCompanyName"
            :aria-busy="sessionInformationLoading"
            @click="openProfilesDialog"
          >
            <span
              v-if="sessionInformationLoading"
              class="app-shell__profile-switcher-label app-shell__skeleton app-shell__skeleton--text app-shell__skeleton--text-medium"
              aria-hidden="true"
            />
            <span v-else class="app-shell__profile-switcher-label">
              {{ currentCompanyName }}
            </span>
            <span
              class="app-shell__profile-switcher-icon material-icons"
              aria-hidden="true"
              :class="{
                'app-shell__profile-switcher-icon--hidden':
                  sessionInformationLoading,
              }"
            >
              arrow_drop_down
            </span>
          </button>
        </div>
      </template>

      <template v-else>
        <div class="app-shell__desktop-topbar-main">
          <div class="app-shell__desktop-topbar-leading">
            <button
              v-if="hasContextualBack"
              type="button"
              class="app-shell__back-button"
              :aria-label="t('wkt.client.main.back')"
              @click="handleBackNavigation"
            >
              <span class="material-icons" aria-hidden="true">arrow_back</span>
            </button>

            <h1 class="app-shell__title">
              {{ currentRouteLabel }}
            </h1>
          </div>

          <div
            v-if="!hasContextualBack"
            class="app-shell__desktop-topbar-trailing"
          >
            <button
              v-if="shellStore.isKioskSession"
              type="button"
              class="app-shell__kiosk-exit"
              @click="exitKiosk()"
            >
              {{ kioskExitLabel }}
            </button>
            <button
              v-if="showTopbarProfileSwitcher"
              type="button"
              class="app-shell__profile-switcher app-shell__profile-switcher--desktop"
              :class="{
                'app-shell__profile-switcher--clickable': canSwitchProfiles,
                'app-shell__profile-switcher--loading':
                  sessionInformationLoading,
              }"
              :disabled="
                sessionInformationLoading ||
                !canSwitchProfiles ||
                switchingProfile
              "
              :aria-label="currentCompanyName"
              :title="currentCompanyName"
              :aria-busy="sessionInformationLoading"
              @click="openProfilesDialog"
            >
              <span
                v-if="sessionInformationLoading"
                class="app-shell__profile-switcher-label app-shell__skeleton app-shell__skeleton--text app-shell__skeleton--text-medium"
                aria-hidden="true"
              />
              <span v-else class="app-shell__profile-switcher-label">
                {{ currentCompanyName }}
              </span>
              <span
                class="app-shell__profile-switcher-icon material-icons"
                aria-hidden="true"
                :class="{
                  'app-shell__profile-switcher-icon--hidden':
                    sessionInformationLoading,
                }"
              >
                arrow_drop_down
              </span>
            </button>
          </div>
        </div>
      </template>
    </header>

    <div
      v-if="showShellChrome && shellStore.isMobile && mobileProfilesMenuVisible"
      class="app-shell__mobile-profile-layer"
      @click.self="closeMobileProfilesMenu"
    >
      <div class="app-shell__mobile-profile-menu">
        <button
          v-for="profile in selectableProfiles"
          :key="
            profile.companyId ?? profile.id ?? profile.companyName ?? 'profile'
          "
          type="button"
          class="app-shell__mobile-profile-item"
          :class="{
            'app-shell__mobile-profile-item--active': isCurrentProfile(profile),
            'app-shell__mobile-profile-item--loading':
              switchingProfileCompanyId === profile.companyId,
          }"
          :disabled="
            !profile.companyId || isCurrentProfile(profile) || switchingProfile
          "
          @click="switchToProfile(profile)"
        >
          <span class="app-shell__mobile-profile-item-name">
            {{ profile.companyName || currentCompanyName }}
          </span>
          <span class="app-shell__mobile-profile-item-meta">
            {{ formatProfileMeta(profile) }}
          </span>
        </button>
      </div>
    </div>

    <main
      class="app-shell__content"
      :class="{
        'app-shell__content--with-topbar': showShellTopbar,
        'app-shell__content--with-bottomnav':
          showShellChrome && shellStore.isMobile && !hasContextualBack,
        'app-shell__content--contextual': hasContextualBack,
        'app-shell__content--with-sidebar': showDesktopSidebar,
        'app-shell__content--with-collapsed-sidebar':
          showDesktopSidebar && !shellStore.sidebarOpen,
      }"
    >
      <router-view />
    </main>

    <nav
      v-if="showShellChrome && shellStore.isMobile && !hasContextualBack"
      class="app-shell__bottomnav"
    >
      <button
        v-for="item in mobileNavigationItems"
        :key="item.routeName"
        type="button"
        class="app-shell__nav-item"
        :class="{
          'app-shell__nav-item--active':
            shellStore.activeRouteName === item.routeName,
        }"
        @click="navigateTo(item.routeName)"
      >
        <span class="app-shell__nav-item-inner">
          <span class="app-shell__nav-icon-bubble">
            <span class="app-shell__nav-icon material-icons" aria-hidden="true">
              {{ item.iconName }}
            </span>
            <span v-if="item.badge > 0" class="app-shell__nav-badge">{{
              item.badge
            }}</span>
          </span>
          <span class="app-shell__nav-label">{{ item.label }}</span>
        </span>
      </button>
    </nav>

    <Dialog
      v-model:visible="profilesDialogVisible"
      modal
      :dismissableMask="!switchingProfile"
      :closable="!switchingProfile"
      :draggable="false"
      class="app-shell__profiles-dialog"
      :style="{ width: 'min(420px, calc(100vw - 24px))' }"
      :header="profileSummary"
    >
      <div class="app-shell__profiles-list mt-2">
        <button
          v-for="profile in selectableProfiles"
          :key="
            profile.companyId ?? profile.id ?? profile.companyName ?? 'profile'
          "
          type="button"
          class="app-shell__profiles-item"
          :class="{
            'app-shell__profiles-item--active': isCurrentProfile(profile),
            'app-shell__profiles-item--loading':
              switchingProfileCompanyId === profile.companyId,
          }"
          :disabled="
            !profile.companyId || isCurrentProfile(profile) || switchingProfile
          "
          @click="switchToProfile(profile)"
        >
          <span class="app-shell__profiles-item-name">
            {{ profile.companyName || currentCompanyName }}
          </span>
          <span class="app-shell__profiles-item-meta">
            {{ formatProfileMeta(profile) }}
          </span>
        </button>
      </div>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import type { FullEmployeeProfile } from "@wkt/api/gwt/gwtDTO";
import { useAlert } from "@wkt/composables/useAlert";
import { useBadges } from "@wkt/composables/useBadges";
import { useGlobalDialogActions } from "@wkt/composables/useGlobalDialogActions";
import { useKioskMode } from "@wkt/composables/useKioskMode";
import { useUtils } from "@wkt/composables/useUtils";
import { router } from "@wkt/router";
import {
  getRouteDefinitionByName,
  shouldUseShellChrome,
} from "@wkt/router/moduleRegistry";
import { useAppDialogsStore } from "@wkt/stores/appDialogs";
import { useCheckinStore } from "@wkt/stores/checkin";
import { useHomeStore } from "@wkt/stores/home";
import { useSessionCatalogStore } from "@wkt/stores/sessionCatalog";
import { useShellStore } from "@wkt/stores/shell";
import { computed, onMounted, onUnmounted, ref, watchEffect } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const { showAlert } = useAlert();
const appDialogsStore = useAppDialogsStore();
const { openLogoutDialog } = useGlobalDialogActions();
const checkinStore = useCheckinStore();
const homeStore = useHomeStore();
const sessionCatalogStore = useSessionCatalogStore();
const shellStore = useShellStore();
const { computeFromEmployee, refreshDocumentsBadge } = useBadges();
const { exitKiosk, resetKioskTimeout, syncKioskRuntime } = useKioskMode();
const { getProfileAssetUrl } = useUtils();

const customLogoUrl = computed(() => {
  const company = sessionCatalogStore.company;
  if (company?.themeEnabled && company.themeAvatarImage) {
    return getProfileAssetUrl(company.themeAvatarImage);
  }
  return null;
});

const profilesDialogVisible = ref(false);
const mobileProfilesMenuVisible = ref(false);
const switchingProfile = ref(false);
const switchingProfileCompanyId = ref<number | null>(null);
const sidebarAvatarErrored = ref(false);
const sessionInformationLoading = computed(() => sessionCatalogStore.hydrating);

const showShellChrome = computed(
  () =>
    shellStore.activeShellMode === "full" &&
    shouldUseShellChrome(shellStore.activeRouteName),
);

const showShellTopbar = computed(
  () =>
    showShellChrome.value && (shellStore.isMobile || hasContextualBack.value),
);

const routeLabelKeys: Record<string, string> = {
  home: "wkt.client.main.lateral-menu.home",
  news: "wkt.client.main.lateral-menu.news",
  checkin: "wkt.client.main.lateral-menu.checkin",
  procedures: "wkt.client.main.lateral-menu.procedures",
  documents: "wkt.client.main.lateral-menu.documents",
  expensesheets: "wkt.client.main.lateral-menu.expensesheets",
  training: "wkt.client.main.lateral-menu.training",
  profile: "wkt.client.main.lateral-menu.more",
  projects: "wkt.client.main.lateral-menu.more",
  vacations: "wkt.client.main.lateral-menu.more",
  vacationsdetail: "wkt.client.main.lateral-menu.more",
  employee: "wkt.client.main.lateral-menu.more",
  employeecalendar: "wkt.client.main.lateral-menu.more",
  hcalendar: "wkt.client.main.lateral-menu.more",
  form: "wkt.client.main.lateral-menu.more",
  review: "wkt.client.main.lateral-menu.more",
  targethours: "wkt.client.main.lateral-menu.more",
  activitydetail: "wkt.client.main.lateral-menu.more",
  survey: "wkt.client.main.lateral-menu.more",
  docbycategory: "wkt.client.main.lateral-menu.documents",
  expensesheetdetail: "wkt.client.main.lateral-menu.expensesheets",
};

const currentRouteDefinition = computed(() =>
  getRouteDefinitionByName(shellStore.activeRouteName),
);

const currentRouteLabel = computed(() => {
  const routeName = shellStore.activeRouteName;
  if (!routeName) {
    return sessionCatalogStore.company?.name ?? "Tweem";
  }

  const titleQueryParam = currentRouteDefinition.value?.shellTitleQueryParam;
  if (titleQueryParam) {
    const rawTitle = router.currentRoute.value.query[titleQueryParam];
    const normalizedTitle = Array.isArray(rawTitle)
      ? (rawTitle.find(
          (value): value is string =>
            typeof value === "string" && value.trim().length > 0,
        ) ?? null)
      : typeof rawTitle === "string" && rawTitle.trim().length > 0
        ? rawTitle
        : null;

    if (normalizedTitle) {
      return normalizedTitle;
    }
  }

  const translationKey =
    currentRouteDefinition.value?.shellTitleKey ?? routeLabelKeys[routeName];
  return translationKey ? t(translationKey) : routeName;
});

const currentBackRouteName = computed(() => {
  const contextualBackRoute = router.currentRoute.value.query.backRoute;
  if (typeof contextualBackRoute === "string" && contextualBackRoute.trim()) {
    const normalizedBackRoute = contextualBackRoute.trim();
    if (getRouteDefinitionByName(normalizedBackRoute)) {
      return normalizedBackRoute;
    }
  }

  const backRouteName = currentRouteDefinition.value?.backRouteName;
  return typeof backRouteName === "string" ? backRouteName : null;
});

const currentBackMode = computed(() => {
  const backMode = currentRouteDefinition.value?.backMode;
  return backMode === "history"
    ? "history"
    : currentBackRouteName.value
      ? "route"
      : null;
});

const hasContextualBack = computed(() => currentBackMode.value !== null);

const showDesktopSidebar = computed(
  () =>
    showShellChrome.value && !shellStore.isMobile && !hasContextualBack.value,
);

const showTopbarProfileSwitcher = computed(
  () => !hasContextualBack.value && !shellStore.isKioskSession,
);

const showMobileKioskExit = computed(
  () =>
    shellStore.isMobile &&
    !hasContextualBack.value &&
    shellStore.isKioskSession,
);

const currentCompanyName = computed(
  () => sessionCatalogStore.company?.name ?? "Tweem",
);

const selectableProfiles = computed<FullEmployeeProfile[]>(() =>
  (sessionCatalogStore.myProfiles ?? []).filter(
    (profile) =>
      profile.isEnabled === true &&
      profile.isCompanyEnabled === true &&
      profile.isCompanyLicenseEnabled === true,
  ),
);

const canSwitchProfiles = computed(
  () => !shellStore.isKioskSession && selectableProfiles.value.length > 1,
);

const currentEmployeeName = computed(() => {
  const employee = sessionCatalogStore.employee;
  if (!employee) {
    return "";
  }

  return [employee.firstname, employee.lastname].filter(Boolean).join(" ");
});

const sidebarAvatarUrl = computed(() => {
  if (sidebarAvatarErrored.value) {
    return "img/default-avatar.jpeg";
  }

  return getProfileAssetUrl(sessionCatalogStore.employee?.profilePicture);
});

const profileSummary = computed(() => {
  const profiles = selectableProfiles.value;
  if (profiles.length <= 1) {
    return currentCompanyName.value;
  }

  return t("wkt.client.shell.available-profiles", { count: profiles.length });
});

const kioskExitLabel = computed(
  () => `${t("wkt.client.main.bottom-exit")} (${shellStore.kioskTimeout})`,
);

interface ShellActionItem {
  id: string;
  label: string;
  iconName: string;
  run: () => void;
  visible: boolean;
}

const shellActionItems = computed<ShellActionItem[]>(() =>
  [
    {
      id: "logout",
      label: t("wkt.client.main.lateral-menu.logout"),
      iconName: "exit_to_app",
      run: () => openLogoutDialog(),
      visible: !shellStore.isKioskSession,
    },
  ].filter((item) => item.visible),
);

interface ShellNavigationItem {
  routeName: string;
  label: string;
  badge: number;
  enabled: boolean;
  iconName: string;
}

function readBooleanFlag(
  source: Record<string, unknown> | null | undefined,
  keys: string[],
  fallback: boolean,
): boolean {
  if (!source) {
    return fallback;
  }

  for (const key of keys) {
    const value = source[key];
    if (typeof value === "boolean") {
      return value;
    }
  }

  return fallback;
}

const navigationItems = computed<ShellNavigationItem[]>(() => {
  const company = sessionCatalogStore.company;

  return [
    {
      routeName: "home",
      label: t("wkt.client.main.bottom-menu.home"),
      badge: shellStore.badges.home ?? 0,
      enabled: true,
      iconName: "home",
    },
    {
      routeName: "news",
      label: t("wkt.client.main.lateral-menu.news"),
      badge: shellStore.badges.news ?? 0,
      enabled: false,
      iconName: "announcement",
    },
    {
      routeName: "checkin",
      label: t("wkt.client.main.bottom-menu.checkin"),
      badge: shellStore.badges.checkin ?? 0,
      enabled: readBooleanFlag(company, ["moduleCheckinEnabled"], true),
      iconName: "update",
    },
    {
      routeName: "procedures",
      label: t("wkt.client.main.bottom-menu.procedures"),
      badge: shellStore.badges.procedures ?? 0,
      enabled: readBooleanFlag(
        company,
        ["moduleProceduresEnabled", "isModuleProceduresEnabled"],
        true,
      ),
      iconName: "transform",
    },
    {
      routeName: "documents",
      label: t("wkt.client.main.lateral-bottom.documents"),
      badge: shellStore.badges.documents ?? 0,
      enabled: readBooleanFlag(
        company,
        ["moduleDocumentsEnabled", "isModuleDocumentsEnabled"],
        true,
      ),
      iconName: "folder_open",
    },
    {
      routeName: "expensesheets",
      label: t("wkt.client.main.lateral-bottom.expensesheets"),
      badge: shellStore.badges.expensesheets ?? 0,
      enabled: readBooleanFlag(
        company,
        ["moduleExpensesEnabled", "isModuleExpensesEnabled"],
        false,
      ),
      iconName: "credit_card",
    },
    {
      routeName: "training",
      label: t("wkt.client.main.bottom-menu.training"),
      badge: shellStore.badges.training ?? 0,
      enabled: readBooleanFlag(company, ["trainingModuleEnabled"], false),
      iconName: "school",
    },
    {
      routeName: "profile",
      label: t("wkt.client.main.bottom-menu.more"),
      badge: 0,
      enabled: true,
      iconName: "more_vert",
    },
  ].filter((item) => item.enabled);
});

const primaryNavigationItems = computed(() =>
  navigationItems.value.filter((item) =>
    ["home", "checkin", "procedures", "documents"].includes(item.routeName),
  ),
);

const secondaryNavigationItems = computed(() =>
  navigationItems.value.filter(
    (item) =>
      !["home", "checkin", "procedures", "documents"].includes(item.routeName),
  ),
);

const mobileNavigationItems = computed(() => {
  const preferredOrder = [
    "home",
    "checkin",
    "procedures",
    "expensesheets",
    "documents",
    "profile",
  ];
  return preferredOrder
    .map((routeName) =>
      navigationItems.value.find((item) => item.routeName === routeName),
    )
    .filter((item): item is ShellNavigationItem => Boolean(item));
});

function handleViewportResize(): void {
  shellStore.updateViewport(window.innerWidth);
}

function isCurrentProfile(profile: FullEmployeeProfile): boolean {
  const companyId = profile.companyId ?? null;
  return (
    companyId !== null &&
    companyId === (sessionCatalogStore.company?.id ?? null)
  );
}

function formatProfileMeta(profile: FullEmployeeProfile): string {
  const employeeName = [profile.firstname, profile.lastname]
    .filter((value): value is string =>
      Boolean(value && value.trim().length > 0),
    )
    .join(" ");

  if (employeeName.length > 0) {
    return employeeName;
  }

  return profile.companyName || currentCompanyName.value;
}

function handleSidebarAvatarError(): void {
  if (sidebarAvatarErrored.value) {
    return;
  }

  sidebarAvatarErrored.value = true;
}

function openProfilesDialog(): void {
  if (!canSwitchProfiles.value || switchingProfile.value) {
    return;
  }

  if (shellStore.isMobile) {
    mobileProfilesMenuVisible.value = !mobileProfilesMenuVisible.value;
    return;
  }

  profilesDialogVisible.value = true;
}

function closeMobileProfilesMenu(): void {
  mobileProfilesMenuVisible.value = false;
}

function switchToProfile(profile: FullEmployeeProfile): void {
  const companyId = profile.companyId ?? null;
  if (
    companyId === null ||
    isCurrentProfile(profile) ||
    switchingProfile.value
  ) {
    return;
  }

  switchingProfile.value = true;
  switchingProfileCompanyId.value = companyId;
  profilesDialogVisible.value = false;
  mobileProfilesMenuVisible.value = false;
  appDialogsStore.$reset();
  void finalizeProfileSwitch(companyId);
}

function resetDerivedStoresForProfileSwitch(): void {
  checkinStore.$reset();
  homeStore.$reset();
  shellStore.setBadges({
    home: 0,
    news: 0,
    checkin: 0,
    procedures: 0,
    documents: 0,
    expensesheets: 0,
    training: 0,
  });
  shellStore.clearPendingReveal();
  shellStore.requestRefresh();
}

async function finalizeProfileSwitch(companyId: number): Promise<void> {
  try {
    const result = await sessionCatalogStore.switchProfile(companyId);
    if (result.outcome === "error") {
      throw result.error;
    }

    if (result.outcome === "login-required") {
      throw new Error("Profile switch requires login");
    }

    const resolvedCompanyId =
      result.outcome === "ready" ||
      result.outcome === "kiosk" ||
      result.outcome === "register"
        ? (result.sessionInformation?.company?.id ?? null)
        : null;

    if (resolvedCompanyId !== null && resolvedCompanyId !== companyId) {
      throw new Error("Profile switch resolved a different company");
    }

    resetDerivedStoresForProfileSwitch();
    shellStore.syncKioskFromStorage();
    computeFromEmployee();
    await refreshDocumentsBadge();
    shellStore.requestRefresh();

    if (result.outcome === "kiosk") {
      await router.push({ name: "kiosk" }).catch(() => undefined);
    } else if (result.outcome === "register") {
      await router.push({ name: "register" }).catch(() => undefined);
    }
  } catch (_) {
    showAlert({
      text: t("wkt.client.profile.error-state.description"),
      color: "error",
    });
  } finally {
    switchingProfile.value = false;
    switchingProfileCompanyId.value = null;
  }
}

function navigateTo(routeName: string): void {
  mobileProfilesMenuVisible.value = false;

  if (shellStore.activeRouteName === routeName) {
    return;
  }

  void router.push({ name: routeName }).catch(() => undefined);
}

function handleBackNavigation(): void {
  mobileProfilesMenuVisible.value = false;

  if (currentBackMode.value === "history") {
    if (window.history.length > 1) {
      window.history.back();
      return;
    }

    if (currentBackRouteName.value) {
      navigateTo(currentBackRouteName.value);
    }

    return;
  }

  if (currentBackRouteName.value) {
    navigateTo(currentBackRouteName.value);
  }
}

function openAdmin(): void {
  const adminUrl = new URL("/admin", window.location.origin).toString();
  window.open(adminUrl, "_blank", "noopener");
}

onMounted(() => {
  shellStore.updateViewport(window.innerWidth);
  shellStore.syncKioskFromStorage();
  computeFromEmployee();
  syncKioskRuntime();
  window.addEventListener("resize", handleViewportResize);
});

onUnmounted(() => {
  window.removeEventListener("resize", handleViewportResize);
});

watchEffect(() => {
  sessionCatalogStore.employee?.profilePicture;
  sidebarAvatarErrored.value = false;
});
</script>

<style>
.app-shell {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 100vh;
  min-height: 100dvh;
  background:
    linear-gradient(
      180deg,
      rgba(var(--shell-rgb, var(--primary-rgb)), 0.08),
      rgba(var(--shell-rgb, var(--primary-rgb)), 0) 22%
    ),
    #f7f9fc;
}

.app-shell__content {
  width: 100%;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  --app-shell-content-min-height: 100%;
}

.app-shell__content--with-topbar {
  box-sizing: border-box;
}

.app-shell__content--with-bottomnav {
  padding-bottom: 80px;
  --app-shell-content-min-height: calc(100vh - 80px);
  --app-shell-content-min-height: calc(100dvh - 80px);
}

.app-shell__content--with-topbar.app-shell__content--with-bottomnav {
  padding: 60px 0 80px;
  --app-shell-content-min-height: calc(100vh - 60px - 80px);
  --app-shell-content-min-height: calc(100dvh - 60px - 80px);
}

.app-shell__content--with-sidebar {
  padding-left: 256px;
}

.app-shell__content--with-collapsed-sidebar {
  padding-left: 76px;
}

.app-shell--mobile {
  width: 100%;
}

.app-shell--desktop {
  width: 100%;
}

.app-shell.kiosk-mode {
  font-size: 1.2em;
}

.app-shell--global-host {
  position: fixed;
  inset: 0;
  z-index: 1200;
  pointer-events: auto;
  overflow-y: auto;
  overscroll-behavior: none;
  -webkit-overflow-scrolling: auto;
  touch-action: pan-y;
}

.app-shell__topbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 22px 16px;
  background: rgba(247, 249, 252, 0.94);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(var(--primary-rgb), 0.12);
}

.app-shell__back-button {
  width: 42px;
  height: 42px;
  border: 0;
  border-radius: 999px;
  background: rgba(var(--primary-rgb), 0.08);
  color: var(--primary-color);
  cursor: pointer;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.app-shell__back-button .material-icons {
  font-size: 24px;
  line-height: 1;
  font-weight: 400;
}

.app-shell__topbar--contextual {
  background: rgba(255, 255, 255, 0.96);
  border-bottom: 1px solid rgba(148, 163, 184, 0.2);
}

.app-shell__topbar--contextual .app-shell__back-button {
  background: rgba(24, 94, 169, 0.12);
  color: #185ea9;
}

.app-shell__sidebar {
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  z-index: 3;
  display: flex;
  flex-direction: column;
  width: 256px;
  padding: 18px 14px;
  box-sizing: border-box;
  background: linear-gradient(
    180deg,
    var(--shell-color, var(--primary-color)),
    var(--shell-color, var(--primary-color))
  );
  color: white;
  border-right: 1px solid rgba(255, 255, 255, 0.08);
}

.app-shell__sidebar--collapsed {
  width: 64px;
}

.app-shell__sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 18px;
}

.app-shell__sidebar-logo {
  display: block;
  width: 178px;
  max-width: 100%;
  height: auto;
  object-fit: contain;
  clip-path: inset(20px 0 20px 70px);
  margin-left: -28px;
  margin-top: -20px;
  margin-bottom: -20px;
}

.app-shell__sidebar-logo--custom {
  flex: 1 1 auto;
  min-width: 0;
  width: auto;
  max-height: 40px;
  object-fit: contain;
  object-position: center;
  clip-path: none;
  margin: 0;
}

.app-shell__header-logo {
  display: block;
  width: 224px;
  max-width: 100%;
  height: auto;
  object-fit: contain;
  clip-path: inset(14px 0 8px 62px);
  margin-left: -59px;
  margin-top: -20px;
  margin-bottom: -20px;
}

.app-shell__header-logo--custom {
  width: auto;
  max-width: 160px;
  height: 36px;
  object-fit: contain;
  object-position: center;
  clip-path: none;
  margin-left: 6px;
  margin-top: 0;
  margin-bottom: 0;
}

.app-shell__sidebar-toggle {
  flex-shrink: 0;
  border: 0;
  border-radius: 12px;
  padding: 8px 10px;
  background: rgba(255, 255, 255, 0.12);
  color: white;
  cursor: pointer;
}

.app-shell__sidebar-body {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 22px;
}

.app-shell__sidebar-profile {
  border: 0;
  background: transparent;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 0 8px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  text-align: left;
}

.app-shell__sidebar-profile--clickable {
  cursor: pointer;
}

.app-shell__sidebar-profile--loading {
  cursor: default;
}

.app-shell__sidebar-profile-avatar,
.app-shell__sidebar-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px;
}

.app-shell__sidebar-profile-avatar {
  width: 42px;
  height: 42px;
  border-radius: 14px;
  object-fit: cover;
  background: rgba(255, 255, 255, 0.18);
  border: 2px solid rgba(255, 255, 255, 0.16);
}

.app-shell__sidebar-icon {
  font-size: 1.45rem;
  line-height: 1;
}

.app-shell__sidebar-profile-copy {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  gap: 4px;
}

.app-shell__sidebar-profile-name {
  font-size: 1rem;
  font-weight: 700;
  color: white;
}

.app-shell__sidebar-profile-meta {
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.72);
}

.app-shell__sidebar-profile-trailing {
  align-self: center;
  font-size: 1.2rem;
  line-height: 1;
  color: rgba(255, 255, 255, 0.72);
}

.app-shell__nav-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.app-shell__nav-group--secondary {
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
}

.app-shell__sidebar-item,
.app-shell__sidebar-action {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  min-height: 46px;
  padding: 0 14px;
  border: 0;
  border-radius: 14px;
  background: transparent;
  color: rgba(255, 255, 255, 0.84);
  cursor: pointer;
  text-align: left;
}

.app-shell__sidebar-item:hover,
.app-shell__sidebar-action:hover {
  background: rgba(255, 255, 255, 0.1);
  color: white;
}

.app-shell__sidebar-action--danger {
  background: rgba(56, 189, 248, 0.16);
  color: white;
}

.app-shell__sidebar-action--danger:hover {
  background: rgba(56, 189, 248, 0.24);
}

.app-shell__sidebar-item--active {
  background: rgba(255, 255, 255, 0.16);
  color: white;
}

.app-shell__sidebar-item-leading,
.app-shell__sidebar-action-leading {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 12px;
}

.app-shell__sidebar-item-label,
.app-shell__sidebar-action-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.95rem;
  font-weight: 600;
}

.app-shell__sidebar-item-badge {
  min-width: 20px;
  padding: 2px 7px;
  border-radius: 999px;
  background: #38bdf8;
  color: #07253d;
  font-size: 0.72rem;
  font-weight: 800;
  text-align: center;
}

.app-shell__sidebar-footer {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
}

.app-shell__sidebar--collapsed .app-shell__sidebar-header {
  justify-content: center;
}

.app-shell__sidebar--collapsed .app-shell__sidebar-logo {
  display: none;
}

.app-shell__sidebar--collapsed .app-shell__sidebar-profile {
  justify-content: center;
  padding-inline: 0;
  padding-bottom: 14px;
}

.app-shell__sidebar--collapsed .app-shell__sidebar-profile-copy,
.app-shell__sidebar--collapsed .app-shell__sidebar-profile-trailing,
.app-shell__sidebar--collapsed .app-shell__sidebar-item-label,
.app-shell__sidebar--collapsed .app-shell__sidebar-action-label {
  display: none;
}

.app-shell__sidebar--collapsed .app-shell__nav-group,
.app-shell__sidebar--collapsed .app-shell__sidebar-footer {
  align-items: center;
}

.app-shell__sidebar--collapsed .app-shell__sidebar-item,
.app-shell__sidebar--collapsed .app-shell__sidebar-action {
  justify-content: center;
  width: 48px;
  min-height: 48px;
  padding: 0;
  border-radius: 16px;
}

.app-shell__sidebar--collapsed .app-shell__sidebar-item-badge {
  position: absolute;
  top: 4px;
  right: 2px;
}

.app-shell__sidebar--collapsed .app-shell__sidebar-profile-avatar {
  width: 38px;
  height: 38px;
}

.app-shell__identity {
  min-width: 0;
}

.app-shell__eyebrow {
  margin: 0 0 4px;
  font-size: 0.78rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(var(--primary-rgb), 0.7);
}

.app-shell__title {
  margin: 0;
  font-size: 1.35rem;
  font-weight: 700;
  color: #12314f;
}

.app-shell__status {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
}

.app-shell__mobile-topbar-main {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.app-shell__mobile-topbar-leading {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 10px;
}

.app-shell__desktop-topbar-main {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
}

.app-shell__desktop-topbar-leading {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 14px;
}

.app-shell__desktop-topbar-leading .app-shell__title {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.app-shell__desktop-topbar-trailing {
  display: flex;
  min-width: 0;
  flex-shrink: 0;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
}

.app-shell__title--mobile {
  color: inherit;
}

.app-shell__profile-switcher {
  display: inline-flex;
  min-width: 0;
  max-width: min(48vw, 240px);
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid rgba(255, 255, 255, 0.9);
  border-radius: 14px;
  background: transparent;
  color: inherit;
}

.app-shell__profile-switcher--desktop {
  max-width: min(34vw, 340px);
  padding: 9px 14px;
  border-color: rgba(var(--primary-rgb), 0.24);
  background: rgba(255, 255, 255, 0.9);
  color: #12314f;
  box-shadow: 0 8px 20px rgba(15, 45, 86, 0.06);
}

.app-shell__profile-switcher--loading {
  cursor: default;
}

.app-shell__profile-switcher--clickable {
  cursor: pointer;
}

.app-shell__profile-switcher-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.95rem;
  font-weight: 600;
}

.app-shell__profile-switcher-icon {
  flex-shrink: 0;
  font-size: 1.25rem;
  line-height: 1;
}

.app-shell__profile-switcher-icon--hidden {
  visibility: hidden;
}

.app-shell__skeleton {
  position: relative;
  overflow: hidden;
  background: rgba(148, 163, 184, 0.22);
  color: transparent;
}

.app-shell__skeleton::after {
  content: "";
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.55),
    transparent
  );
  animation: app-shell-skeleton-shimmer 1.25s ease-in-out infinite;
}

.app-shell__skeleton--circle {
  border-radius: 999px;
}

.app-shell__skeleton--text {
  display: block;
  width: 100%;
  min-height: 0.9rem;
  border-radius: 999px;
}

.app-shell__skeleton--text-medium {
  width: 8.5rem;
  height: 0.95rem;
}

.app-shell__skeleton--text-short {
  width: 5.5rem;
  height: 0.76rem;
}

@keyframes app-shell-skeleton-shimmer {
  100% {
    transform: translateX(100%);
  }
}

.app-shell__mobile-profile-layer {
  position: fixed;
  inset: calc(56px + env(safe-area-inset-top, 0px)) 0 0;
  z-index: 3;
  background: rgba(15, 23, 42, 0.16);
}

.app-shell__mobile-profile-menu {
  margin: 8px 12px 0 auto;
  width: min(320px, calc(100vw - 24px));
  padding: 8px;
  border: 1px solid rgba(186, 216, 243, 0.7);
  border-radius: 18px;
  background: #ffffff;
  box-shadow: 0 18px 36px rgba(15, 45, 86, 0.18);
}

.app-shell__mobile-profile-item {
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 3px;
  padding: 11px 12px;
  border: 0;
  border-radius: 14px;
  background: transparent;
  color: #12314f;
  text-align: left;
}

.app-shell__mobile-profile-item--active {
  background: rgba(var(--primary-rgb), 0.08);
}

.app-shell__mobile-profile-item--loading {
  opacity: 0.6;
}

.app-shell__mobile-profile-item-name {
  font-size: 0.94rem;
  font-weight: 700;
}

.app-shell__mobile-profile-item-meta {
  color: #5f7389;
  font-size: 0.8rem;
}

.app-shell__kiosk-exit,
.app-shell__kiosk-badge,
.app-shell__badge-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 34px;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 700;
  color: white;
  background: var(--shell-color, var(--primary-color));
}

.app-shell__kiosk-exit {
  border: 0;
  cursor: pointer;
}

.app-shell__badge-pill {
  background: #0f766e;
}

.app-shell__user {
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.92rem;
  font-weight: 600;
  color: #3b4e63;
}

.app-shell__user-block {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}

.app-shell__user-trigger {
  border: 0;
  background: transparent;
  padding: 0;
  color: inherit;
  text-align: right;
}

.app-shell__user-trigger--clickable {
  cursor: pointer;
}

.app-shell__user-meta {
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.74rem;
  color: #6b7d90;
}

.app-shell__bottomnav {
  position: fixed;
  left: 0;
  right: 0;
  bottom: -1px;
  z-index: 2;
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(0, 1fr);
  align-items: start;
  gap: 2px;
  min-height: calc(64px + env(safe-area-inset-bottom, 0px));
  padding: 8px 4px calc(8px + env(safe-area-inset-bottom, 0px));
  box-sizing: border-box;
  background: linear-gradient(
    180deg,
    var(--shell-color, var(--primary-color)),
    var(--shell-color, var(--primary-color))
  );
  border-top: 1px solid rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(10px);
  box-shadow: 0 -10px 28px rgba(15, 35, 58, 0.12);
}

.app-shell__nav-item {
  display: flex;
  min-width: 0;
  align-items: flex-start;
  justify-content: center;
  min-height: 60px;
  border: 0;
  border-radius: 14px;
  padding: 0;
  appearance: none;
  -webkit-appearance: none;
  background: transparent;
  color: rgba(255, 255, 255, 0.82);
  cursor: pointer;
  outline: none;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  transition:
    background-color 160ms ease,
    color 160ms ease,
    transform 160ms ease;
}

.app-shell__nav-item:focus,
.app-shell__nav-item:active {
  outline: none;
  background: transparent;
}

.app-shell__nav-item--active {
  color: white;
}

.app-shell__nav-item-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 5px;
  min-width: 0;
  width: 100%;
  padding: 1px 2px 0;
  box-sizing: border-box;
}

.app-shell__nav-icon-bubble {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 12px;
  transition:
    background-color 160ms ease,
    box-shadow 160ms ease,
    transform 160ms ease;
}

.app-shell__nav-icon {
  font-size: 1.15rem;
  line-height: 1;
}

.app-shell__nav-label {
  display: block;
  width: 100%;
  min-height: 1.45rem;
  overflow: hidden;
  font-size: 0.6rem;
  font-weight: 700;
  line-height: 1.08;
  white-space: normal;
  text-align: center;
  color: inherit;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
}

.app-shell__nav-badge {
  position: absolute;
  top: -3px;
  right: -6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 4px;
  border-radius: 999px;
  background: #38bdf8;
  color: #07253d;
  box-shadow: 0 4px 10px rgba(56, 189, 248, 0.28);
  font-size: 0.64rem;
  font-weight: 800;
  line-height: 1;
  white-space: nowrap;
}

.app-shell__nav-item--active .app-shell__nav-icon-bubble {
  background: rgba(255, 255, 255, 0.16);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.06);
}

.app-shell__nav-item--active .app-shell__nav-icon,
.app-shell__nav-item--active .app-shell__nav-label {
  color: white;
}

.app-shell__nav-item:focus-visible .app-shell__nav-icon-bubble {
  background: rgba(255, 255, 255, 0.14);
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.24);
}

.app-shell__nav-item:active .app-shell__nav-icon-bubble {
  transform: scale(0.96);
}

@media (hover: hover) {
  .app-shell__nav-item:hover .app-shell__nav-icon-bubble {
    background: rgba(255, 255, 255, 0.1);
  }
}

@media (max-width: 420px) {
  .app-shell__bottomnav {
    gap: 1px;
    padding-inline: 2px;
  }

  .app-shell__nav-item {
    min-height: 58px;
  }

  .app-shell__nav-icon-bubble {
    width: 32px;
    height: 32px;
    border-radius: 11px;
  }

  .app-shell__nav-icon {
    font-size: 1.4rem;
  }

  .app-shell__nav-label {
    font-size: 0.6rem;
    min-height: 1.38rem;
  }

  .app-shell__nav-badge {
    min-width: 16px;
    height: 16px;
    font-size: 0.58rem;
  }
}

.app-shell__profiles-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.app-shell__profiles-item {
  width: 100%;
  border: 1px solid rgba(148, 163, 184, 0.3);
  background: #ffffff;
  border-radius: 14px;
  padding: 14px 16px;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: #0f172a;
  cursor: pointer;
  transition:
    border-color 0.18s ease,
    box-shadow 0.18s ease,
    transform 0.18s ease;
}

.app-shell__profiles-item:hover:not(.app-shell__profiles-item--loading) {
  border-color: #9ab7d8;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
  transform: translateY(-1px);
}

.app-shell__profiles-item--active {
  border-color: rgba(var(--primary-rgb), 0.45);
  background: rgba(var(--primary-rgb), 0.08);
}

.app-shell__profiles-item--loading {
  opacity: 0.6;
}

.app-shell__profiles-item-name {
  font-size: 0.95rem;
  font-weight: 700;
}

.app-shell__profiles-item-meta {
  font-size: 0.82rem;
  color: #64748b;
}

@media (min-width: 992px) {
  .app-shell__content {
    /*padding-bottom: 24px;*/
    height: 100vh;
    --app-shell-content-min-height: calc(100vh - 24px);
    --app-shell-content-min-height: calc(100dvh - 24px);
  }

  .app-shell__content--with-topbar {
    padding-top: 72px;
    --app-shell-content-min-height: calc(100vh - 72px - 24px);
    --app-shell-content-min-height: calc(100dvh - 72px - 24px);
  }

  .app-shell__content--with-bottomnav {
    padding-bottom: 24px;
    --app-shell-content-min-height: calc(100vh - 24px);
    --app-shell-content-min-height: calc(100dvh - 24px);
  }

  .app-shell__topbar {
    padding: 14px 22px 13px;
  }

  .app-shell__topbar--with-sidebar {
    left: 256px;
  }

  .app-shell__sidebar--collapsed ~ .app-shell__topbar--with-sidebar {
    left: 64px;
  }

  .app-shell__profile-switcher--desktop .app-shell__profile-switcher-label {
    font-size: 0.92rem;
    font-weight: 700;
  }

  .app-shell__bottomnav {
    display: none;
  }
}

@media (max-width: 991px) {
  .app-shell__content--with-topbar.app-shell__content--with-bottomnav {
    padding-top: calc(56px + env(safe-area-inset-top, 0px));
    --app-shell-content-min-height: calc(
      100vh - 56px - env(safe-area-inset-top, 0px) - 80px
    );
    --app-shell-content-min-height: calc(
      100dvh - 56px - env(safe-area-inset-top, 0px) - 80px
    );
  }

  .app-shell__content--with-topbar.app-shell__content--contextual {
    padding-top: calc(56px + env(safe-area-inset-top, 0px));
    --app-shell-content-min-height: calc(
      100vh - 56px - env(safe-area-inset-top, 0px)
    );
    --app-shell-content-min-height: calc(
      100dvh - 56px - env(safe-area-inset-top, 0px)
    );
  }

  .app-shell__content--with-topbar.app-shell__content--contextual:not(
      .app-shell__content--with-bottomnav
    ) {
    padding-bottom: 24px;
    --app-shell-content-min-height: calc(
      100vh - 56px - env(safe-area-inset-top, 0px) - 24px
    );
    --app-shell-content-min-height: calc(
      100dvh - 56px - env(safe-area-inset-top, 0px) - 24px
    );
  }

  .app-shell__topbar {
    padding-top: calc(10px + env(safe-area-inset-top, 0px)) !important;
    padding-right: 16px !important;
    padding-bottom: 9px !important;
    padding-left: 12px !important;
    gap: 12px;
    background: var(--shell-color, var(--primary-color));
    border-bottom: 0;
    color: white;
  }

  .app-shell__topbar--contextual {
    background: rgba(255, 255, 255, 0.96);
    border-bottom: 1px solid rgba(148, 163, 184, 0.2);
    color: var(--primary-color);
  }

  .app-shell__back-button {
    padding: 8px 12px;
    font-size: 0.82rem;
    background: rgba(255, 255, 255, 0.12);
    color: white !important;
  }

  .app-shell__topbar--contextual .app-shell__back-button {
    background: rgba(24, 94, 169, 0.12);
    color: var(--primary-color) !important;
  }

  .app-shell__title {
    font-size: 1.5rem;
    color: white;
  }

  .app-shell__topbar--contextual .app-shell__title {
    color: var(--primary-color);
  }

  .app-shell__eyebrow {
    display: none;
  }

  .app-shell__mobile-topbar-main {
    gap: 10px;
  }

  .app-shell__mobile-topbar-leading {
    gap: 8px;
  }

  .app-shell__title--mobile {
    font-size: 1.1rem;
    line-height: 1.05;
  }

  .app-shell__profile-switcher {
    max-width: min(52vw, 232px);
    padding: 7px 11px;
    border-radius: 12px;
  }

  .app-shell__profile-switcher-label {
    font-size: 0.9rem;
  }
}
</style>
